import { z } from 'zod';
/**
 * Admin-facing Zod schemas for reply templates (canned responses).
 *
 * MVP scope: team-wide templates (no personal vs shared split, no categories).
 * Body is plain text with `{{token}}` interpolation applied client-side when
 * the agent inserts a template into the composer.
 */
export const ReplyTemplateId = z.guid().brand();
export const ReplyTemplateDTO = z.object({
    id: ReplyTemplateId,
    name: z.string().min(1).max(200),
    /** Optional subject seeded into the composer when the agent picks this
     *  template. Stays null to let the composer keep the conversation's own
     *  subject. */
    subject: z.string().max(500).nullable(),
    /** HTML body. The composer renders it in a WYSIWYG editor; send path
     *  fills `html` on the outbound and derives plain text by stripping tags. */
    body: z.string().min(1).max(64_000),
    /** Optional data-provider binding. When set, the bound provider runs at
     *  send time to resolve `{{data.<provider>.<field>}}` tokens. Null → no
     *  external data fetch (the case for all pre-existing templates). */
    dataProviderKey: z.string().min(1).nullable(),
    createdByAdminId: z.guid().nullable(),
    updatedByAdminId: z.guid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int().positive(),
});
export const ReplyTemplateListResponse = z.object({
    items: z.array(ReplyTemplateDTO),
});
export const CreateReplyTemplateRequest = z.object({
    name: z
        .string()
        .min(1, 'Required')
        .max(200, 'Must be 200 characters or fewer')
        .meta({ label: 'Name', placeholder: 'e.g. Refund confirmation' }),
    subject: z
        .string()
        .max(500, 'Too long')
        .nullable()
        .optional()
        .meta({ label: 'Subject', placeholder: 'e.g. Subscription cancelled' }),
    body: z.string().min(1, 'Required').max(64_000, 'Too long').meta({
        label: 'Body',
        placeholder: '<p>Hi {{contact.name}},</p><p>Thanks…</p>',
    }),
    dataProviderKey: z.string().min(1).nullable().optional().meta({ label: 'Data provider' }),
});
export const UpdateReplyTemplateRequest = CreateReplyTemplateRequest.partial().refine((v) => v.name !== undefined ||
    v.body !== undefined ||
    v.subject !== undefined ||
    v.dataProviderKey !== undefined, { message: 'At least one of name, subject, body, or dataProviderKey must be provided' });
/**
 * Tokens recognized by the client-side interpolator. Kept here so both the
 * composer and the template editor's help text render from the same list.
 */
export const TEMPLATE_TOKENS = [
    { token: '{{contact.name}}', description: "Recipient's display name (falls back to 'there')" },
    { token: '{{contact.email}}', description: "Recipient's primary email" },
    { token: '{{agent.name}}', description: "Signed-in agent's display name" },
    { token: '{{conversation.id}}', description: 'Conversation UUID' },
    {
        token: '{{conversation.number}}',
        description: 'Short case number the customer can quote (e.g. 100042)',
    },
    { token: '{{conversation.subject}}', description: 'Conversation subject (may be empty)' },
];
const escapeTokenHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/**
 * Interpolate `{{token}}`s in `body` against `ctx`. Unknown tokens are left
 * as-is so the agent notices and can fill them by hand. Missing known tokens
 * use sensible fallbacks ('there' for name, empty string otherwise).
 *
 * `opts.escapeHtml` — when true, the substituted VALUES are HTML-escaped. Pass
 * it when interpolating into an HTML **body** so a customer-controlled value
 * (e.g. a display name set to `<img src=…>`) can never inject markup. Leave it
 * off (default) for plain-text contexts like the email **subject** header, where
 * escaping would surface literal `&amp;` entities. Unknown tokens (left as the
 * literal `{{…}}`) are never escaped.
 *
 * Pure function — exported for both the composer insert path and tests.
 */
export const interpolateTemplateBody = (body, ctx, opts) => {
    const esc = opts?.escapeHtml ? escapeTokenHtml : (s) => s;
    return body.replace(/\{\{\s*([a-z]+\.[a-z]+)\s*\}\}/gi, (match, token) => {
        const key = token.toLowerCase();
        switch (key) {
            case 'contact.name':
                return esc(ctx.contact?.name?.trim() || 'there');
            case 'contact.email':
                return esc(ctx.contact?.email ?? '');
            case 'agent.name':
                return esc(ctx.agent?.name?.trim() || 'Support');
            case 'conversation.id':
                return esc(ctx.conversation?.id ?? '');
            case 'conversation.number': {
                const n = ctx.conversation?.number;
                return esc(n === undefined || n === null ? '' : String(n));
            }
            case 'conversation.subject':
                return esc(ctx.conversation?.subject ?? '');
            default:
                return match;
        }
    });
};
//# sourceMappingURL=templates.js.map